class PlpHeading extends HTMLElement {
  connectedCallback() {
    document.body.classList.add('has-plp-heading');
    this.sectionWrapper = this.closest('.shopify-section');
    if (this.sectionWrapper) this.sectionWrapper.classList.add('plp-heading-sticky');

    // Capture the full URL once at page load — active state never changes after this.
    const currentUrl = window.location.pathname + window.location.search;

    this.nav = this.querySelector('.plp-heading__nav');
    this.swiper = null;

    if (this.nav) {
      this.initSwiper();
      this.markActiveLink(currentUrl);
    }

    // this.trackSticky();
    this.bindFilterButton();

    // Re-run offset calculations now that plp-heading-sticky class is applied,
    // and whenever this section's height changes (e.g. content reflow).
    if (window.StickyOffsetManager) {
      window.StickyOffsetManager.update();
      if (this.sectionWrapper) {
        this._resizeObserver = new ResizeObserver(() => window.StickyOffsetManager.update());
        this._resizeObserver.observe(this.sectionWrapper);
      }
    }
    this.watchProductCount();
    this.updateFilterCount();
    this.watchFilterCount();
  }

  disconnectedCallback() {
    if (this.productCountObserver) this.productCountObserver.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this.swiper) this.swiper.destroy(true, true);

    if (!document.querySelector('plp-heading')) {
      document.body.classList.remove('has-plp-heading');
    }
  }

  trackSticky() {
    if (!this.sectionWrapper) return;

    const sectionHeader = document.querySelector('.section-header');
    const plpMenuSection = document.querySelector('.shopify-section.plp-menu-sticky');
    let rafId = null;

    const updateTop = () => {
      const headerBottom = sectionHeader ? Math.max(0, sectionHeader.getBoundingClientRect().bottom) : 0;
      const top = plpMenuSection
        ? Math.max(headerBottom, plpMenuSection.getBoundingClientRect().bottom)
        : headerBottom;
      // this.sectionWrapper.style.top = `${top}px`;
    };

    const loopUpdate = () => {
      updateTop();
      rafId = requestAnimationFrame(loopUpdate);
    };

    window.addEventListener('scroll', updateTop, { passive: true });

    if (sectionHeader) {
      sectionHeader.addEventListener('transitionstart', (e) => {
        if (e.propertyName !== 'top') return;
        cancelAnimationFrame(rafId);
        loopUpdate();
      });

      sectionHeader.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'top') return;
        cancelAnimationFrame(rafId);
        updateTop();
      });
    }

    updateTop();
  }

  markActiveLink(currentUrl) {
    let activeItem = null;
    let activeIndex = -1;

    this.querySelectorAll('.plp-heading__link').forEach((link, index) => {
      // Normalise to pathname + search so protocol/host differences don't matter.
      const url = new URL(link.href, window.location.origin);
      const linkUrl = url.pathname + url.search;

      if (linkUrl === currentUrl) {
        link.classList.add('plp-heading__link--active');
        link.setAttribute('aria-current', 'page');
        activeItem = link.parentElement; // the <li>
        activeIndex = index;
      }
    });

    if (activeItem) {
      this.scrollActiveIntoView(activeItem, activeIndex);
    }
  }

  scrollActiveIntoView(item, index) {
    if (this.swiper && index > -1) {
      this.swiper.slideTo(index, 0);
      return;
    }

    this.nav.scrollLeft = item.offsetLeft;
  }

  initSwiper() {
    if (!this.nav) return;

    // Swiper is no longer a deferred <script>; fetch it on demand instead of
    // waiting for the load event (which fired long after this nav was usable).
    if (typeof Swiper === 'undefined') {
      window.loadSwiper().then(() => this.initSwiper());
      return;
    }

    this.swiper = new Swiper(this.nav, {
      loop: false,
      slidesPerView: 'auto',
      spaceBetween: 0,
      freeMode: true,
      simulateTouch: true,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
      },
    });
  }

  bindFilterButton() {
    const btn = this.querySelector('.plp-heading__filter-btn');
    if (!btn) return;

    btn.addEventListener('click', () => {
      // Delegate to the existing mobile facets drawer trigger on the page.
      const drawerTrigger = document.querySelector('.mobile-facets__open-wrapper');
      if (drawerTrigger) {
        drawerTrigger.click();
        return;
      }

      const drawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
      const details = drawer?.querySelector('details.mobile-facets__disclosure');
      const summary = details?.querySelector('summary');
      if (drawer && details && summary) {
        details.setAttribute('open', '');
        if (typeof drawer.openMenuDrawer === 'function') {
          drawer.openMenuDrawer(summary);
        } else {
          summary.click();
        }
      }
    });
  }

  isCountableFilterParam(key) {
    return key.startsWith('filter.') && key !== 'filter.p.m.custom.activate_look_product';
  }

  getActiveFilterCount() {
    const params = new URLSearchParams(window.location.search);
    let count = 0;
    for (const key of params.keys()) {
      if (this.isCountableFilterParam(key)) count++;
    }
    if (params.has('sort_by')) count++;
    return count;
  }

  updateFilterCount() {
    const count = this.getActiveFilterCount();
    const params = new URLSearchParams(window.location.search);
    const hasFilters = [...params.keys()].some((k) => this.isCountableFilterParam(k));
    document.querySelectorAll('[data-filter-count]').forEach((badge) => {
      badge.textContent = hasFilters ? count : '';
    });
  }

  watchFilterCount() {
    // Update badge whenever Shopify's facet form re-renders the page section.
    document.addEventListener('facets:filter-applied', () => this.updateFilterCount());
    document.addEventListener('facets:filter-removed', () => this.updateFilterCount());

    // Fallback: intercept pushState/replaceState to catch URL changes from AJAX filtering.
    const patch = (method) => {
      const original = history[method];
      history[method] = (...args) => {
        original.apply(history, args);
        this.updateFilterCount();
      };
    };
    patch('pushState');
    patch('replaceState');
  }

  watchProductCount() {
    const countEl = this.querySelector('[data-plp-count]');
    if (!countEl) return;

    const updateHeadingCount = (source) => {
      if (!source) return;

      const count = source.dataset.count ?? (source.textContent.match(/\d+/) || [])[0];
      if (count !== undefined) {
        if (count > 0) {
          countEl.textContent = count;
          countEl.style.display = '';
        } else {
          countEl.textContent = '';
          countEl.style.display = 'none';
        }
      }
    };

    const syncFromDom = () => {
      const source = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
      updateHeadingCount(source);
    };

    const bindObserver = () => {
      const source = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
      if (!source) return;

      if (this.productCountObserver) this.productCountObserver.disconnect();

      this.productCountObserver = new MutationObserver(() => syncFromDom());
      this.productCountObserver.observe(source, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      syncFromDom();
    };

    const patch = () => {
      bindObserver();

      if (typeof FacetFiltersForm === 'undefined') return;
      if (FacetFiltersForm.__plpHeadingCountPatched) return;

      const original = FacetFiltersForm.renderProductCount;
      FacetFiltersForm.renderProductCount = function (html) {
        original.call(this, html);
        bindObserver();

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.getElementById('ProductCountDesktop') || parsed.getElementById('ProductCount');
        updateHeadingCount(source);
      };

      FacetFiltersForm.__plpHeadingCountPatched = true;
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', patch);
    } else {
      patch();
    }
  }
}

customElements.define('plp-heading', PlpHeading);
